import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { placeholderToken } from "@/lib/placeholderVocabulary";

/**
 * Shared Insert Placeholder control used by Report Designer and SmartPhrase Builder.
 * Callers supply the placeholder groups and decide how a selected token is inserted.
 */
export default function InsertPlaceholderMenu({
  groups = [],
  disabled,
  testId,
  itemTestIdPrefix,
  formatToken = placeholderToken,
  onSelect,
  searchable = false,
  searchTestId,
  contentClassName = "w-64 max-h-80 overflow-y-auto",
}) {
  const [query, setQuery] = useState("");

  const visibleGroups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!searchable || !needle) return groups;
    return groups
      .map((group) => ({
        ...group,
        fields: group.fields.filter((field) => {
          const token = formatToken(field.token);
          return (
            String(field.label || "")
              .toLowerCase()
              .includes(needle) ||
            String(field.token || "")
              .toLowerCase()
              .includes(needle) ||
            String(token || "")
              .toLowerCase()
              .includes(needle)
          );
        }),
      }))
      .filter((group) => group.fields.length > 0);
  }, [formatToken, groups, query, searchable]);

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (!open) setQuery("");
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className="h-7 rounded-md px-2 text-xs"
          onMouseDown={(event) => event.preventDefault()}
        >
          Insert Placeholder
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className={contentClassName}>
        {searchable ? (
          <div className="px-2 pb-1.5 pt-1" onMouseDown={(event) => event.preventDefault()}>
            <input
              type="search"
              value={query}
              data-testid={searchTestId}
              placeholder="Search placeholders…"
              className="h-7 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
            />
          </div>
        ) : null}
        {visibleGroups.length === 0 ? (
          <div className="px-2 py-3 text-xs text-slate-400">No matching placeholders.</div>
        ) : (
          visibleGroups.map((group, index) => (
            <div key={group.id}>
              {index > 0 || searchable ? <DropdownMenuSeparator /> : null}
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">
                {group.label}
              </DropdownMenuLabel>
              {group.fields.map((field) => (
                <DropdownMenuItem
                  key={field.token}
                  data-testid={
                    itemTestIdPrefix ? `${itemTestIdPrefix}-placeholder-${field.token}` : undefined
                  }
                  onSelect={() => onSelect?.(field.token, field.label)}
                >
                  {field.label}
                  <span className="ml-auto font-fhir text-[10px] text-slate-400">
                    {formatToken(field.token)}
                  </span>
                </DropdownMenuItem>
              ))}
            </div>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
